import { createReducer, on } from '@ngrx/store';
import { EqualizerPreset, PRESET_LIBRARY } from './objects/media-player/equalizer-preset';
import * as EqualizerActions from './equalizer.actions';

export interface EqualizerState {
  presets: EqualizerPreset[];
  currentPreset: EqualizerPreset;
  isEnabled: boolean;
  customPresets: EqualizerPreset[];
}

export const initialEqualizerState: EqualizerState = {
  presets: PRESET_LIBRARY,
  currentPreset: PRESET_LIBRARY[0],
  isEnabled: true,
  customPresets: []
};

export const equalizerReducer = createReducer(
  initialEqualizerState,

  on(EqualizerActions.loadPresetsFromStorageSuccess, (state, { presets }) => {
    // Merge stored custom presets with default presets
    const customPresets = presets.filter(p => p.isCustom);
    return {
      ...state,
      customPresets,
      presets: [...PRESET_LIBRARY, ...customPresets]
    };
  }),

  // Restores the preset the user last had selected, together with any manual
  // band tweaks and the enabled flag. Gains are matched by frequency so a
  // stored preset still restores if the band list ever changes.
  on(EqualizerActions.restoreEqualizerSettings, (state, { presetId, bands, isEnabled }) => {
    const stored = state.presets.find(p => p.id === presetId);
    if (!stored) {
      return { ...state, isEnabled };
    }

    const storedGains = new Map(bands.map(b => [b.frequency, b.gain]));
    const restoredBands = stored.bands.map(b =>
      storedGains.has(b.frequency) ? { ...b, gain: storedGains.get(b.frequency)! } : { ...b }
    );
    const currentPreset = { ...stored, bands: restoredBands, isActive: true };

    return {
      ...state,
      isEnabled,
      currentPreset,
      presets: state.presets.map(p =>
        p.id === currentPreset.id ? currentPreset : { ...p, isActive: false }
      )
    };
  }),

  on(EqualizerActions.selectPreset, (state, { preset }) => {
    const updatedPresets = state.presets.map(p => ({
      ...p,
      isActive: p.id === preset.id
    }));
    return {
      ...state,
      currentPreset: preset,
      presets: updatedPresets
    };
  }),

  on(EqualizerActions.updateBand, (state, { bandIndex, gain }) => {
    const updatedBands = [...state.currentPreset.bands];
    updatedBands[bandIndex] = { ...updatedBands[bandIndex], gain };
    const updatedPreset = { ...state.currentPreset, bands: updatedBands };

    const updatedPresets = state.presets.map(p =>
      p.id === updatedPreset.id ? updatedPreset : p
    );

    return {
      ...state,
      currentPreset: updatedPreset,
      presets: updatedPresets
    };
  }),

  on(EqualizerActions.enableEqualizer, (state) => ({
    ...state,
    isEnabled: true
  })),

  on(EqualizerActions.disableEqualizer, (state) => ({
    ...state,
    isEnabled: false
  })),

  on(EqualizerActions.createCustomPreset, (state, { name, bands }) => {
    const newPreset: EqualizerPreset = {
      id: `custom-${Date.now()}`,
      name,
      bands,
      isCustom: true,
      isActive: false
    };

    const updatedCustomPresets = [...state.customPresets, newPreset];
    const updatedPresets = [...state.presets, newPreset];

    return {
      ...state,
      customPresets: updatedCustomPresets,
      presets: updatedPresets
    };
  }),

  on(EqualizerActions.deleteCustomPreset, (state, { presetId }) => {
    const updatedCustomPresets = state.customPresets.filter(p => p.id !== presetId);
    const updatedPresets = state.presets.filter(p => p.id !== presetId);

    // If deleted preset was active, switch to Normal
    let currentPreset = state.currentPreset;
    if (currentPreset.id === presetId) {
      currentPreset = PRESET_LIBRARY[0];
    }

    return {
      ...state,
      customPresets: updatedCustomPresets,
      presets: updatedPresets,
      currentPreset
    };
  }),

  on(EqualizerActions.updateCustomPreset, (state, { presetId, name, bands }) => {
    const updatedCustomPresets = state.customPresets.map(p =>
      p.id === presetId
        ? {
            ...p,
            name: name || p.name,
            bands: bands || p.bands
          }
        : p
    );

    const updatedPresets = state.presets.map(p =>
      p.id === presetId
        ? {
            ...p,
            name: name || p.name,
            bands: bands || p.bands
          }
        : p
    );

    const updatedCurrentPreset = state.currentPreset.id === presetId
      ? {
          ...state.currentPreset,
          name: name || state.currentPreset.name,
          bands: bands || state.currentPreset.bands
        }
      : state.currentPreset;

    return {
      ...state,
      customPresets: updatedCustomPresets,
      presets: updatedPresets,
      currentPreset: updatedCurrentPreset
    };
  })
);
